import type {
  MenuItem,
  MenuLinkType,
  PutAdminV1StoresStoreIdMenusIdItemsBodyItemsItem,
} from '@nucleus-commerce/api-client/admin';

/** Header > dropdown > column: the backend refuses a fourth level. */
export const MAX_DEPTH = 3;
export const MAX_ITEMS = 200;
export const MAX_SIBLINGS = 50;

/** One menu item while editing; `key` is local and never sent. */
export interface MenuNode {
  key: string;
  title: string;
  linkType: MenuLinkType;
  targetId: string | null;
  /** For url links the stored url; for targets the storefront path the target resolves to. */
  url: string | null;
  openInNewTab: boolean;
  /** Set by the backend when the target was deleted, unpublished or hidden. */
  targetMissing: boolean;
  children: MenuNode[];
}

/** The index path of a node: `[2, 0]` is the first child of the third top-level item. */
export type NodePath = readonly number[];

let sequence = 0;
export function newKey(): string {
  sequence += 1;
  return `node-${sequence}`;
}

export function emptyNode(): MenuNode {
  return {
    key: newKey(),
    title: '',
    linkType: 'url',
    targetId: null,
    url: '',
    openInNewTab: false,
    targetMissing: false,
    children: [],
  };
}

interface StoredItem extends Omit<MenuItem, 'children'> {
  children: readonly unknown[];
}

function isStoredItem(value: unknown): value is StoredItem {
  return typeof value === 'object' && value !== null && 'linkType' in value && 'title' in value;
}

export function fromMenuItems(items: readonly unknown[]): MenuNode[] {
  return items.filter(isStoredItem).map((item) => ({
    key: newKey(),
    title: item.title,
    linkType: item.linkType,
    targetId: item.targetId,
    url: item.url,
    openInNewTab: item.openInNewTab,
    targetMissing: item.targetMissing,
    children: fromMenuItems(item.children),
  }));
}

/** The PUT body's tree: url links carry `url`, every other link carries `targetId`. */
export function toMenuItemsInput(
  nodes: readonly MenuNode[],
): PutAdminV1StoresStoreIdMenusIdItemsBodyItemsItem[] {
  return nodes.map((node) => ({
    title: node.title.trim(),
    linkType: node.linkType,
    ...(node.linkType === 'url' ? { url: (node.url ?? '').trim() } : { targetId: node.targetId }),
    openInNewTab: node.openInNewTab,
    children: toMenuItemsInput(node.children),
  }));
}

export function countNodes(nodes: readonly MenuNode[]): number {
  return nodes.reduce((sum, node) => sum + 1 + countNodes(node.children), 0);
}

/** Levels below and including the node: a leaf is 1. */
export function subtreeDepth(node: MenuNode): number {
  return 1 + Math.max(0, ...node.children.map(subtreeDepth));
}

export function nodeAt(nodes: readonly MenuNode[], path: NodePath): MenuNode | undefined {
  let level: readonly MenuNode[] = nodes;
  let found: MenuNode | undefined;
  for (const index of path) {
    found = level[index];
    if (!found) return undefined;
    level = found.children;
  }
  return found;
}

/** Replaces the sibling list that holds `path`'s last index, cloning only along the path. */
function withSiblings(
  nodes: readonly MenuNode[],
  parentPath: NodePath,
  change: (siblings: MenuNode[]) => MenuNode[],
): MenuNode[] {
  if (parentPath.length === 0) return change([...nodes]);
  const [head, ...rest] = parentPath;
  return nodes.map((node, index) =>
    index === head ? { ...node, children: withSiblings(node.children, rest, change) } : node,
  );
}

export function updateNode(
  nodes: readonly MenuNode[],
  path: NodePath,
  patch: Partial<Omit<MenuNode, 'key' | 'children'>>,
): MenuNode[] {
  const parent = path.slice(0, -1);
  const index = path[path.length - 1] ?? 0;
  return withSiblings(nodes, parent, (siblings) =>
    siblings.map((node, i) => (i === index ? { ...node, ...patch } : node)),
  );
}

export function removeNode(nodes: readonly MenuNode[], path: NodePath): MenuNode[] {
  const parent = path.slice(0, -1);
  const index = path[path.length - 1] ?? 0;
  return withSiblings(nodes, parent, (siblings) => siblings.filter((_, i) => i !== index));
}

/** Appends at the top level (empty path) or as the last child of the node at `parentPath`. */
export function appendNode(
  nodes: readonly MenuNode[],
  parentPath: NodePath,
  node: MenuNode,
): MenuNode[] {
  if (parentPath.length === 0) return [...nodes, node];
  const parent = parentPath.slice(0, -1);
  const index = parentPath[parentPath.length - 1] ?? 0;
  return withSiblings(nodes, parent, (siblings) =>
    siblings.map((sibling, i) =>
      i === index ? { ...sibling, children: [...sibling.children, node] } : sibling,
    ),
  );
}

export function moveNode(nodes: readonly MenuNode[], path: NodePath, offset: -1 | 1): MenuNode[] {
  const parent = path.slice(0, -1);
  const index = path[path.length - 1] ?? 0;
  return withSiblings(nodes, parent, (siblings) => {
    const target = index + offset;
    if (target < 0 || target >= siblings.length) return siblings;
    const [moved] = siblings.splice(index, 1);
    if (moved) siblings.splice(target, 0, moved);
    return siblings;
  });
}

/** Whether the node can become the last child of its previous sibling without a fourth level. */
export function canIndent(nodes: readonly MenuNode[], path: NodePath): boolean {
  const index = path[path.length - 1] ?? 0;
  const node = nodeAt(nodes, path);
  return node !== undefined && index > 0 && path.length + subtreeDepth(node) <= MAX_DEPTH;
}

export function indentNode(nodes: readonly MenuNode[], path: NodePath): MenuNode[] {
  const node = nodeAt(nodes, path);
  if (!node || !canIndent(nodes, path)) return [...nodes];
  const index = path[path.length - 1] ?? 0;
  const previous = [...path.slice(0, -1), index - 1];
  return appendNode(removeNode(nodes, path), previous, node);
}

/** Moves the node out of its parent, right after the parent. */
export function outdentNode(nodes: readonly MenuNode[], path: NodePath): MenuNode[] {
  if (path.length < 2) return [...nodes];
  const node = nodeAt(nodes, path);
  if (!node) return [...nodes];
  const parentPath = path.slice(0, -1);
  const grandParent = parentPath.slice(0, -1);
  const parentIndex = parentPath[parentPath.length - 1] ?? 0;
  const without = removeNode(nodes, path);
  return withSiblings(without, grandParent, (siblings) => {
    siblings.splice(parentIndex + 1, 0, node);
    return siblings;
  });
}

export type MenuItemProblem =
  | 'titleRequired'
  | 'titleLength'
  | 'urlRequired'
  | 'urlFormat'
  | 'targetRequired'
  | 'tooDeep'
  | 'tooManySiblings';

/** Client-side checks mirroring the backend's, keyed by node key. */
export function validateMenuTree(nodes: readonly MenuNode[]): Map<string, MenuItemProblem> {
  const problems = new Map<string, MenuItemProblem>();
  const walk = (level: readonly MenuNode[], depth: number) => {
    level.forEach((node, index) => {
      const title = node.title.trim();
      const url = (node.url ?? '').trim();
      if (index >= MAX_SIBLINGS) problems.set(node.key, 'tooManySiblings');
      else if (depth > MAX_DEPTH) problems.set(node.key, 'tooDeep');
      else if (!title) problems.set(node.key, 'titleRequired');
      else if (title.length > 120) problems.set(node.key, 'titleLength');
      else if (node.linkType === 'url' && !url) problems.set(node.key, 'urlRequired');
      else if (node.linkType === 'url' && !url.startsWith('/') && !url.startsWith('https://')) {
        problems.set(node.key, 'urlFormat');
      } else if (node.linkType !== 'url' && !node.targetId) {
        problems.set(node.key, 'targetRequired');
      }
      walk(node.children, depth + 1);
    });
  };
  walk(nodes, 1);
  return problems;
}

/**
 * The node a backend field points at. The service reports `body.items[0].children[1].url`; the
 * boundary validator reports `body.items.0.children.1.children` for a fourth level.
 */
export function nodeKeyForField(nodes: readonly MenuNode[], field: string): string | null {
  const normalised = field.replace(/\[(\d+)\]/g, '.$1');
  const match = /^(?:body\.)?items\.(\d+(?:\.children\.\d+)*)(?:\.|$)/.exec(normalised);
  if (!match?.[1]) return null;
  const path = match[1].split('.children.').map(Number);
  return nodeAt(nodes, path)?.key ?? null;
}
